import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, FileText, FileUp, History } from "lucide-react";
import { errMsg } from "@shared/api/errors";
import {
  CertificationButton,
  CertificationOverviewCard,
  CertificationSectionHeader,
  CertificationTabs,
} from "../../ui";
import { manualSistemaIntegradoApi } from "../api/manual-sistema-integrado";
import type {
  ManualSistemaIntegrado,
  ManualSistemaIntegradoControlModificacion,
  ManualSistemaIntegradoControlPayload,
  ManualSistemaIntegradoUploadPayload,
} from "../types/manual-sistema-integrado";
import { ManualSistemaIntegradoControlDocumentalPanel } from "./ManualSistemaIntegradoControlDocumentalPanel";
import { ManualSistemaIntegradoControlModificacionDeleteModal } from "./ManualSistemaIntegradoControlModificacionDeleteModal";
import { ManualSistemaIntegradoControlModificacionFormModal } from "./ManualSistemaIntegradoControlModificacionFormModal";
import { ManualSistemaIntegradoUploadModal } from "./ManualSistemaIntegradoUploadModal";
import { ManualSistemaIntegradoStructuredPanel } from "./ManualSistemaIntegradoStructuredPanel";
import { ManualSistemaIntegradoViewer } from "./ManualSistemaIntegradoViewer";
import "../styles/manual-sistema-integrado.css";

type Standard = "iso-9001-14001" | "iso-27001-27701";
type Tab = "manual" | "structured" | "control";

type Props = {
  standard: Standard;
  onBack: () => void;
};

type ControlFormState = {
  open: boolean;
  row: ManualSistemaIntegradoControlModificacion | null;
};

export function ManualSistemaIntegradoSection({ standard, onBack }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>("manual");
  const [manual, setManual] = useState<ManualSistemaIntegrado | null>(null);
  const [available, setAvailable] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [documentBlob, setDocumentBlob] = useState<Blob | null>(null);
  const [controlRows, setControlRows] = useState<ManualSistemaIntegradoControlModificacion[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [controlForm, setControlForm] = useState<ControlFormState>({ open: false, row: null });
  const [controlSubmitting, setControlSubmitting] = useState(false);
  const [controlError, setControlError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ManualSistemaIntegradoControlModificacion | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [structuredMounted, setStructuredMounted] = useState(false);

  const load = useCallback(async (refresh = false) => {
    refresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    setPreviewError(null);
    setControlError(null);

    try {
      const state = await manualSistemaIntegradoApi.getState();
      setManual(state.data);
      setAvailable(state.disponible);
      setCanManage(state.can_manage);

      const controlsPromise = manualSistemaIntegradoApi
        .listControlDocumental()
        .then(setControlRows)
        .catch((requestError) => {
          setControlRows([]);
          setControlError(errMsg(requestError, "No se pudo cargar el control documental."));
        });

      if (state.disponible) {
        try {
          setDocumentBlob(await manualSistemaIntegradoApi.getPreview());
        } catch (requestError) {
          setDocumentBlob(null);
          setPreviewError(errMsg(requestError, "No se pudo descargar el Word para su previsualización."));
        }
      } else {
        setDocumentBlob(null);
      }

      await controlsPromise;
    } catch (requestError) {
      setError(errMsg(requestError, "No se pudo cargar el Manual del Sistema Integrado."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (standard === "iso-9001-14001" && activeTab === "structured") {
      setStructuredMounted(true);
    }
  }, [activeTab, standard]);

  const tabs = useMemo(() => {
    const items: Array<{ id: Tab; label: string; count: number }> = [
      { id: "manual", label: "Manual", count: available ? 1 : 0 },
    ];
    if (standard === "iso-9001-14001") {
      items.push({ id: "structured", label: "Manual estructurado", count: 1 });
    }
    items.push({ id: "control", label: "Control documental", count: controlRows.length });
    return items;
  }, [available, controlRows.length, standard]);

  const handleUpload = async (payload: ManualSistemaIntegradoUploadPayload) => {
    setUploading(true);
    setUploadError(null);
    try {
      await manualSistemaIntegradoApi.upload(payload);
      setUploadOpen(false);
      await load(true);
    } catch (requestError) {
      setUploadError(errMsg(requestError, "No se pudo guardar el manual."));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await manualSistemaIntegradoApi.download();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = manual?.nombre_docx || "manual-sistema-integrado.docx";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (requestError) {
      setPreviewError(errMsg(requestError, "No se pudo descargar el manual."));
    }
  };

  const handleControlSubmit = async (payload: ManualSistemaIntegradoControlPayload) => {
    setControlSubmitting(true);
    setControlError(null);
    try {
      if (controlForm.row) {
        await manualSistemaIntegradoApi.updateControlDocumental(controlForm.row.id, payload);
      } else {
        await manualSistemaIntegradoApi.createControlDocumental(payload);
      }
      setControlForm({ open: false, row: null });
      await load(true);
    } catch (requestError) {
      setControlError(errMsg(requestError, "No se pudo guardar la modificación."));
    } finally {
      setControlSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await manualSistemaIntegradoApi.deleteControlDocumental(deleteTarget.id);
      setDeleteTarget(null);
      await load(true);
    } catch (requestError) {
      setControlError(errMsg(requestError, "No se pudo eliminar la modificación."));
    } finally {
      setDeleting(false);
    }
  };

  const standardLabel = standard === "iso-9001-14001" ? "ISO 9001-14001" : "ISO 27001-27701";

  return (
    <section className="manual-si-section">
      <CertificationSectionHeader
        eyebrow="Documento compartido"
        title="Manual del Sistema Integrado"
        description="Consulta el Word vigente compartido por los dos estándares y descarga el archivo original cuando necesites modificarlo."
        standardLabel={standardLabel}
        onBack={onBack}
        onRefresh={() => void load(true)}
        refreshing={refreshing}
      />

      <div className="iso27001-overview-grid">
        <CertificationOverviewCard icon={FileText} label="Manual" value={available ? 1 : 0} />
        <CertificationOverviewCard icon={History} label="Control documental" value={controlRows.length} />
      </div>

      <CertificationTabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
        ariaLabel="Contenido del Manual del Sistema Integrado"
      />

      {activeTab === "manual" ? (
        <section className="iso27001-table-card manual-si-document-card">
          <div className="iso27001-table-header manual-si-document-toolbar">
            <div className="manual-si-document-copy">
              <div className="iso27001-table-eyebrow">Documento vigente</div>
              <h3>{manual?.titulo ?? "Manual del Sistema Integrado"}</h3>
              <p>
                {manual
                  ? `Edición ${manual.edicion} · ${manual.fecha_edicion}${manual.usuario_modificacion_nombre ? ` · ${manual.usuario_modificacion_nombre}` : ""}`
                  : "Sin documento cargado"}
              </p>
            </div>
            <div className="manual-si-document-actions">
              {available ? (
                <CertificationButton
                  variant="secondary"
                  icon={<Download size={16} />}
                  onClick={() => void handleDownload()}
                >
                  Descargar Word
                </CertificationButton>
              ) : null}
              {canManage ? (
                <CertificationButton
                  variant="primary"
                  icon={<FileUp size={16} />}
                  onClick={() => {
                    setUploadError(null);
                    setUploadOpen(true);
                  }}
                >
                  {available ? "Sustituir manual" : "Cargar manual"}
                </CertificationButton>
              ) : null}
            </div>
          </div>

          {error ? (
            <div className="iso27001-feedback-state manual-si-feedback-state manual-si-feedback-error">
              <div className="iso27001-feedback-title">No se pudo cargar el manual</div>
              <p>{error}</p>
              <CertificationButton variant="secondary" onClick={() => void load(true)}>
                Reintentar
              </CertificationButton>
            </div>
          ) : (
            <ManualSistemaIntegradoViewer
              documentBlob={documentBlob}
              loading={loading}
              error={previewError}
            />
          )}
        </section>
      ) : null}

      {standard === "iso-9001-14001" && structuredMounted ? (
        <div hidden={activeTab !== "structured"}>
          <ManualSistemaIntegradoStructuredPanel canManage={canManage} />
        </div>
      ) : null}

      {activeTab === "control" ? (
        <ManualSistemaIntegradoControlDocumentalPanel
          rows={controlRows}
          loading={loading}
          error={error ?? controlError}
          canManage={canManage}
          onRetry={() => void load(true)}
          onCreate={() => {
            setControlError(null);
            setControlForm({ open: true, row: null });
          }}
          onEdit={(row) => {
            setControlError(null);
            setControlForm({ open: true, row });
          }}
          onDelete={setDeleteTarget}
        />
      ) : null}

      <ManualSistemaIntegradoUploadModal
        open={uploadOpen}
        manual={manual}
        submitting={uploading}
        error={uploadError}
        onClose={() => setUploadOpen(false)}
        onSubmit={(payload) => void handleUpload(payload)}
      />

      <ManualSistemaIntegradoControlModificacionFormModal
        open={controlForm.open}
        row={controlForm.row}
        submitting={controlSubmitting}
        error={controlError}
        onClose={() => setControlForm({ open: false, row: null })}
        onSubmit={(payload) => void handleControlSubmit(payload)}
      />

      <ManualSistemaIntegradoControlModificacionDeleteModal
        open={deleteTarget !== null}
        deleting={deleting}
        description={deleteTarget ? `Se eliminará la modificación registrada en ${deleteTarget.fecha}.` : ""}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void handleDelete()}
      />
    </section>
  );
}
